import type { PayloadAction } from "@reduxjs/toolkit";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { setShippingMethodsOnCart } from "@store/slices/cartSlice/actions/setShippingMethodsOnCart";
import type { OrderException } from "@store/types/OrderExceptions.types";
import type { TradeInOrderAttributes } from "@store/types/TradeInOrderAttributes.types";
import load from "load-script";

import { type Address, type GetPaymentMethodsOptions } from "@jti/fe-common";
import type { OrderAttributeValues } from "@jti/magento";
import { getCurrentMagentoClient } from "@jti/magento";
import { CasAddressesApi, CasProfileApi, convertCasAddressToStoredAddress, PgsApi } from "@jti/rest-api";

import { fetchOrderDeliveryLimits } from "./actions/fetchOrderDeliveryLimits";
import { getDefaultShippingMethods } from "./actions/getDefaultShippingMethods";
import { getPGSToken } from "./actions/getPGSToken";
import { placeOrder } from "./actions/placeOrder";

import type { Checkout } from "./LEGACY__checkoutSlice.types";

export const fetchOrderAttributes = createAsyncThunk("checkout/fetchOrderAttributes", async (isFreeTrial: boolean) => {
  const magentoClient = getCurrentMagentoClient(isFreeTrial);
  return magentoClient.getOrderAttributes();
});

export const fetchAddresses = createAsyncThunk("checkout/fetchAddresses", async () => {
  const casAddressesApi = CasAddressesApi.init();

  return casAddressesApi.getAddresses();
});

export const fetchAccountContact = createAsyncThunk("checkout/fetchAccountContact", async () => {
  const casProfileApi = CasProfileApi.init();
  return casProfileApi.getProfile();
});

export const getPgsPaymentMethods = createAsyncThunk(
  "checkout/pgsPaymentMethods",
  async (options?: GetPaymentMethodsOptions) => {
    const pgsApi = PgsApi.init();

    return await pgsApi.getPaymentMethods(options);
  }
);

export const getPgsClient = createAsyncThunk(
  "checkout/pgsClient",
  async ({ code, locale }: { code: string; locale?: string }) => {
    const pgsApi = PgsApi.init();

    return await pgsApi.getClient(code, locale);
  }
);

export const getPgsAdapter = createAsyncThunk(
  "checkout/pgsAdapter",
  async ({ code, locale }: { code: string; locale?: string }) => {
    const pgsApi = PgsApi.init();
    const pgsApiClient = await pgsApi.getClient(code, locale);

    return {
      client: pgsApiClient,
      paymentCode: code,
    };
  }
);

export const loadPgsAdapter = createAsyncThunk("checkout/loadPgsAdapter", async (adapterUrl: string) => {
  const loadAction = (url: string) =>
    new Promise((resolve, reject) => {
      load(url, function (error) {
        if (error === null) {
          return resolve(url);
        }

        reject(error);
      });
    });

  return await loadAction(adapterUrl);
});

export const createAddress = createAsyncThunk("checkout/createAddress", async ({ address }: { address: Address }) => {
  const casAddressesApi = CasAddressesApi.init();

  return casAddressesApi.createAddress(address);
});

export const updateAgeVerification = createAsyncThunk(
  "checkout/updateAgeVerification",
  async ({ avpUuid }: { avpUuid: string }) => {
    const casProfileApi = CasProfileApi.init();
    return casProfileApi.updateAgeVerification(avpUuid);
  }
);

export const initialCheckoutState: Checkout = {
  country: {
    countryName: "",
    countryCode: "",
  },
  addresses: {
    fetchStatus: "idle",
    data: [],
  },
  shippingMethods: {
    fetchStatus: "idle",
    data: [],
  },
  orderDeliveryLimits: {
    fetchStatus: "idle",
    data: { mode: "", limits: [] },
  },
  orderAttributes: {
    fetchStatus: "idle",
    data: [],
  },
  tradeInAttributes: undefined,
  profileDataFetchStatus: "idle",
  isAgeVerified: null,
  consentPurposes: [],
  shippingSelectedAddressId: undefined,
  accountContact: undefined,
  isSelectShippingAddress: false,
  placingOrder: false,
  paymentToken: "",
  isPaymentMethodsListLoaded: false,
  pgsPaymentMethods: [],
  pgsClient: "",
  isPgsClientPending: false,
  isPaymentProcessing: false,
  loadedPgsClient: "",
  pgsAdapter: {
    client: "",
    paymentCode: "",
  },
  setShippingMethodsOnCartPending: false,
  updateAgeVerificationPending: false,
  isAgeVerificationSuccess: false,
  error: {
    visible: false,
    exception: null,
  },
};

/**
 * @deprecated
 */
export const LEGACY__checkoutSlice = createSlice({
  name: "LEGACY__checkout",
  initialState: initialCheckoutState,

  reducers: {
    setCountry(state, action: PayloadAction<Checkout["country"]>) {
      state.country = action.payload;
    },

    toggleSelectShippingAddress(state, action: PayloadAction<boolean>) {
      state.isSelectShippingAddress = action.payload;
    },

    clearShippingSelectedAddressId(state) {
      state.shippingSelectedAddressId = undefined;
    },

    setShippingSelectedAddressId(state, action: PayloadAction<string | undefined>) {
      state.shippingSelectedAddressId =
        action.payload ?? state.addresses.data.find((address) => address.isDefaultShipping)?.id;
    },

    updateOrderAttributes(state, action: PayloadAction<OrderAttributeValues>) {
      state.orderAttributes.data = state.orderAttributes.data.map((item) => {
        const isOrderAttributeFound = Object.prototype.hasOwnProperty.call(action.payload, item.code);
        if (isOrderAttributeFound) {
          return { ...item, value: action.payload[item.code] };
        }
        return item;
      });
    },

    updateTradeInOrderAttributes(state, action: PayloadAction<TradeInOrderAttributes | null>) {
      state.tradeInAttributes = action.payload;
    },

    showError(state, action: PayloadAction<OrderException>) {
      state.error.exception = action.payload;
      state.error.visible = true;
    },

    hideError(state) {
      state.error.exception = null;
      state.error.visible = false;
    },

    setIsPgsClientPending(state, action: PayloadAction<boolean>) {
      state.isPgsClientPending = action.payload;
    },

    setIsPaymentProcessing(state, action: PayloadAction<boolean>) {
      state.isPaymentProcessing = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(getDefaultShippingMethods.pending, (state) => {
      state.shippingMethods.fetchStatus = "pending";
    });
    builder.addCase(getDefaultShippingMethods.rejected, (state) => {
      state.shippingMethods.fetchStatus = "error";
    });
    builder.addCase(getDefaultShippingMethods.fulfilled, (state, action) => {
      const { methods } = action.payload;
      state.shippingMethods.data = methods;
      state.shippingMethods.fetchStatus = "success";
    });

    builder.addCase(fetchAddresses.pending, (state) => {
      state.addresses.fetchStatus = "pending";
    });

    builder.addCase(fetchAddresses.rejected, (state) => {
      state.addresses.fetchStatus = "error";
    });

    builder.addCase(fetchAddresses.fulfilled, (state, action) => {
      const { addresses } = action.payload;

      state.addresses.fetchStatus = "success";
      state.addresses.data = addresses;
    });

    builder.addCase(fetchOrderDeliveryLimits.pending, (state) => {
      state.orderDeliveryLimits.fetchStatus = "pending";
    });

    builder.addCase(fetchOrderDeliveryLimits.fulfilled, (state, action) => {
      state.orderDeliveryLimits.data = action.payload;
      state.orderDeliveryLimits.fetchStatus = "success";
    });

    builder.addCase(fetchOrderDeliveryLimits.rejected, (state) => {
      state.orderDeliveryLimits.fetchStatus = "error";
    });

    builder.addCase(fetchOrderAttributes.pending, (state) => {
      state.orderDeliveryLimits.fetchStatus = "pending";
    });

    builder.addCase(fetchOrderAttributes.fulfilled, (state, action) => {
      state.orderAttributes.data = action.payload;
      state.orderAttributes.fetchStatus = "success";
    });

    builder.addCase(fetchOrderAttributes.rejected, (state) => {
      state.orderAttributes.fetchStatus = "error";
    });

    builder.addCase(fetchAccountContact.fulfilled, (state, action) => {
      const {
        personalInfo: { firstname, lastname, telephone, dob },
        is_age_verified,
        purposes,
        addresses,
      } = action.payload;
      state.accountContact = { firstname, lastname, telephone, dob };
      state.isAgeVerified = is_age_verified;
      state.consentPurposes = purposes;

      if (addresses) {
        state.addresses.data = addresses.map(convertCasAddressToStoredAddress);
        state.addresses.fetchStatus = "success";
      }

      state.profileDataFetchStatus = "success";
    });

    builder.addCase(fetchAccountContact.pending, (state) => {
      state.profileDataFetchStatus = "pending";
    });

    builder.addCase(fetchAccountContact.rejected, (state) => {
      state.profileDataFetchStatus = "error";
    });

    builder.addCase(placeOrder.pending, (state) => {
      state.placingOrder = true;
    });

    builder.addCase(placeOrder.fulfilled, (state, action) => {
      state.placingOrder = false;
      state.selectedAddressForPayment = action.payload.shippingAddress;
    });

    builder.addCase(placeOrder.rejected, (state, action) => {
      state.placingOrder = false;
      state.error.exception = action.payload || null;
      state.error.visible = true;
    });

    builder.addCase(getPGSToken.pending, (state) => {
      state.placingOrder = true;
    });

    builder.addCase(getPGSToken.fulfilled, (state, action) => {
      state.placingOrder = false;
      state.paymentToken = action.payload;
    });

    builder.addCase(getPGSToken.rejected, (state, action) => {
      state.error.exception = action.payload || null;
      state.error.visible = true;
    });

    builder.addCase(getPgsPaymentMethods.fulfilled, (state, action) => {
      state.pgsPaymentMethods = action.payload.data;
      state.isPaymentMethodsListLoaded = true;
    });

    builder.addCase(getPgsClient.fulfilled, (state, action) => {
      state.pgsClient = action.payload;
    });

    builder.addCase(getPgsAdapter.pending, (state) => {
      state.isPgsClientPending = true;
    });

    builder.addCase(getPgsAdapter.fulfilled, (state, action) => {
      state.pgsAdapter = action.payload;
    });

    builder.addCase(loadPgsAdapter.fulfilled, (state, action) => {
      state.loadedPgsClient = action.payload as string;
      state.isPgsClientPending = false;
    });

    builder.addCase(setShippingMethodsOnCart.pending, (state) => {
      state.setShippingMethodsOnCartPending = true;
    });

    builder.addCase(setShippingMethodsOnCart.fulfilled, (state) => {
      state.setShippingMethodsOnCartPending = false;
    });
    builder.addCase(updateAgeVerification.pending, (state) => {
      state.updateAgeVerificationPending = true;
    });
    builder.addCase(updateAgeVerification.fulfilled, (state, action) => {
      const { is_age_verified } = action.payload;

      state.updateAgeVerificationPending = false;
      state.isAgeVerified = is_age_verified;
      state.isAgeVerificationSuccess = is_age_verified;
    });
  },
});

export const {
  setCountry,
  toggleSelectShippingAddress,
  setShippingSelectedAddressId,
  clearShippingSelectedAddressId,
  updateOrderAttributes,
  updateTradeInOrderAttributes,
  showError,
  hideError,
  setIsPgsClientPending,
  setIsPaymentProcessing,
} = LEGACY__checkoutSlice.actions;
